var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo, useRef, useState, useEffect } from "react";
import { generatePoints, getBounds, generateMinAndMax, getXDates, getYTicks, bisectDate, generateBands, } from "./ForecastUtils";
import * as d3 from "d3";
import { formatNumber } from "../../utils/Functions";
import cx from "classnames";
import { Device } from "../../utils/Device";
import "./ForecastGraph.scss";
const plotRightMarginMobile = 0;
const PLOT_BOTTOM_MARGIN = 22;
const PLOT_TOP_MARGIN = 0;
const getForecastMessages = (args) => {
    return {
        tooShort: "För tidsperioder under ett år estimeras inte utveckling.",
        probability: `Bedömd sannolikhet att du om ${args.years} år och ${args.months} månader`,
        veryGoodText: `Mer än ${formatNumber(args.veryGoodSum || 0, 0)} SEK är ${args.veryGoodPercentage} %`,
        goodText: `Mer än ${formatNumber(args.goodSum || 0, 0)} SEK är ${args.goodPercentage} %`,
        badText: `Mindre än ${formatNumber(args.badSum || 0, 0)} SEK är ${args.badPercentage} %`,
        veryBadText: `Mindre än ${formatNumber(args.veryBadSum || 0, 0)} SEK är ${args.veryBadPercentage} %`,
    };
};
export function ReactForecast(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    var { getTexts = getForecastMessages } = _a, props = __rest(_a, ["getTexts"]);
    const plot = useRef(null);
    const mainGroup = useRef(null);
    const startingCapital = parseInt(props.initial || "0", 10);
    const rightMargin = plotRightMarginMobile;
    const [areas, setAreas] = useState({
        w: 0,
        h: 0,
    });
    const width = areas.w - rightMargin;
    const [showTooltip, setShowTooltip] = useState(false);
    const [mousePosition, setMouseposition] = useState({ x: 0, y: 0 });
    const x = d3.scaleTime().range([0, width]);
    const y = d3.scaleLinear().range([areas.h, 0]);
    useEffect(() => {
        const plotElement = plot.current;
        const redraw = () => {
            var _a, _b;
            const plotBox = (_b = (_a = plotElement === null || plotElement === void 0 ? void 0 : plotElement.parentElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) !== null && _b !== void 0 ? _b : {
                width: 0,
                height: 0,
            };
            setAreas({
                w: plotBox.width,
                h: plotBox.height - PLOT_BOTTOM_MARGIN - PLOT_TOP_MARGIN - 10,
            });
        };
        redraw();
        window.addEventListener("resize", redraw);
        window.addEventListener("orientationchange", redraw);
        return () => {
            window.removeEventListener("resize", redraw);
            window.removeEventListener("orientationchange", redraw);
        };
    }, [setAreas]);
    const points = useMemo(() => {
        return generatePoints({
            initial: props.initial,
            interval: props.interval,
            monthly: props.monthly,
            risk: props.risk,
        });
    }, [props.initial, props.interval, props.monthly, props.risk]);
    const extremeValues = useMemo(() => generateMinAndMax({
        initial: props.initial,
        interval: props.interval,
        monthly: props.monthly,
        risk: props.risk,
    }), [props.initial, props.interval, props.monthly, props.risk]);
    const bounds = useMemo(() => getBounds(extremeValues.max), [
        extremeValues.max,
    ]);
    const minDate = points.badSeries[0].date.toDate();
    const maxDate = points.badSeries[points.badSeries.length - 1].date.toDate();
    x.domain([minDate, maxDate]);
    y.domain([bounds.min, bounds.value]);
    const bands = useMemo(() => {
        return generateBands(x, y, points, startingCapital);
    }, [points, x, y, startingCapital]);
    const eventShowTooltip = () => {
        setShowTooltip(true);
    };
    const eventHideTooltip = () => {
        setShowTooltip(false);
    };
    const onMouseMove = (event) => {
        const boundingBox = event.currentTarget.getBoundingClientRect();
        const cursorPosition = {
            x: event.clientX - boundingBox.left,
            y: event.clientY - boundingBox.top,
        };
        setMouseposition(cursorPosition);
    };
    const onTouchMove = (event) => {
        console.log(event.touches[0]);
        const boundingBox = event.currentTarget.getBoundingClientRect();
        const cursorPosition = {
            x: event.touches[0].clientX - boundingBox.left,
            y: event.touches[0].clientY - boundingBox.top,
        };
        setMouseposition(cursorPosition);
    };
    const hoveredDate = x.invert(mousePosition.x);
    const hoverIdx = bisectDate(points.badSeries, hoveredDate, 1);
    const tooltipLeft = hoverIdx > points.badSeries.length / 2;
    const texts = getTexts({
        years: Math.floor(hoverIdx / 12),
        months: hoverIdx % 12,
        veryGoodSum: ((_b = points.veryGoodSeries[hoverIdx]) === null || _b === void 0 ? void 0 : _b.worth) || 0,
        veryGoodPercentage: 10,
        goodSum: ((_c = points.goodSeries[hoverIdx]) === null || _c === void 0 ? void 0 : _c.worth) || 0,
        goodPercentage: 40,
        badSum: ((_d = points.badSeries[hoverIdx]) === null || _d === void 0 ? void 0 : _d.worth) || 0,
        badPercentage: 40,
        veryBadSum: ((_e = points.veryBadSeries[hoverIdx]) === null || _e === void 0 ? void 0 : _e.worth) || 0,
        veryBadPercentage: 10,
    });
    return (React.createElement("div", { className: cx("plot forecast-graph react-forecast", {
            "show-tooltip": showTooltip,
        }) },
        React.createElement("div", { className: cx("forecast-tooltip", {
                "on-left": tooltipLeft && !Device.isTouchDevice(),
                "on-right": !tooltipLeft && !Device.isTouchDevice(),
            }), style: {
                bottom: !Device.isTouchDevice()
                    ? Math.min(areas.h + 70 - y(((_f = points.veryGoodSeries[hoverIdx]) === null || _f === void 0 ? void 0 : _f.worth) - 34), areas.h + PLOT_BOTTOM_MARGIN)
                    : areas.h + PLOT_BOTTOM_MARGIN + 13,
                left: !Device.isTouchDevice() && !tooltipLeft
                    ? mousePosition.x - 1
                    : "auto",
                right: !Device.isTouchDevice() && tooltipLeft
                    ? areas.w - mousePosition.x - 1
                    : "auto",
            } },
            React.createElement("span", { className: "forecast-tooltip-arrow-border" }),
            hoverIdx < 12 ? (React.createElement("div", { className: "graph-tooltip-short-interval" }, texts.tooShort)) : (React.createElement("div", { className: "graph-tooltip-inner" },
                React.createElement("div", { className: "forecast-tooltip-header" }, texts.probability),
                React.createElement("ul", null,
                    React.createElement("li", { className: "forecast-tooltip-very-good" },
                        React.createElement("div", null,
                            React.createElement("span", { className: "very-good-marker" }),
                            React.createElement("span", { className: "marker-value" }, texts.veryGoodText))),
                    React.createElement("li", { className: "forecast-tooltip-good" },
                        React.createElement("div", null,
                            React.createElement("span", { className: "good-marker" }),
                            React.createElement("span", { className: "marker-value" }, texts.goodText))),
                    React.createElement("li", { className: "forecast-tooltip-bad" },
                        React.createElement("div", null,
                            React.createElement("span", { className: "bad-marker" }),
                            React.createElement("span", { className: "marker-value" }, texts.badText))),
                    React.createElement("li", { className: "forecast-tooltip-very-bad" },
                        React.createElement("div", null,
                            React.createElement("span", { className: "very-bad-marker" }),
                            React.createElement("span", { className: "marker-value" }, texts.veryBadText)))))),
            React.createElement("span", { className: "forecast-tooltip-arrow" })),
        React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", className: "plot-graph", ref: plot },
            React.createElement("g", { transform: `translate(0, ${PLOT_TOP_MARGIN})`, ref: mainGroup },
                React.createElement("line", { className: "border-bottom", x1: "0", x2: width, y1: areas.h, y2: areas.h }),
                React.createElement("line", { className: "border-right", x1: width, x2: width, y1: "0", y2: areas.h }),
                React.createElement("path", { className: "full-band", d: (_g = bands.fullBand(points.veryGoodSeries)) !== null && _g !== void 0 ? _g : undefined, fill: "url(#gradient)" }),
                React.createElement("path", { className: "medium-band", d: (_h = bands.mediumBand(points.goodSeries)) !== null && _h !== void 0 ? _h : undefined, fill: "url(#gradient)" }),
                React.createElement("path", { className: "warning-band", d: (_j = bands.textAnchor(points.warningSeries)) !== null && _j !== void 0 ? _j : undefined }),
                React.createElement("line", { x1: mousePosition.x, x2: mousePosition.x, className: "ruler", y1: 0, y2: areas.h }),
                React.createElement("circle", { cx: "0", cy: "0", r: "4", className: "marker very-bad-marker", transform: `translate(${mousePosition.x},${y((_k = points.veryBadSeries[hoverIdx]) === null || _k === void 0 ? void 0 : _k.worth)})` }),
                React.createElement("circle", { cx: "0", cy: "0", r: "4", className: "marker bad-marker", transform: `translate(${mousePosition.x},${y((_l = points.badSeries[hoverIdx]) === null || _l === void 0 ? void 0 : _l.worth)})` }),
                React.createElement("circle", { cx: "0", cy: "0", r: "4", className: "marker good-marker", transform: `translate(${mousePosition.x},${y((_m = points.goodSeries[hoverIdx]) === null || _m === void 0 ? void 0 : _m.worth)})` }),
                React.createElement("circle", { cx: "0", cy: "0", r: "4", className: "marker very-good-marker", transform: `translate(${mousePosition.x},${y((_o = points.veryGoodSeries[hoverIdx]) === null || _o === void 0 ? void 0 : _o.worth)})` }),
                React.createElement("g", { className: "axis-x" }, getXDates(minDate, maxDate).map((date, idx, arr) => {
                    let textAnchor = "middle";
                    if (idx === 0) {
                        textAnchor = "start";
                    }
                    else if (idx === arr.length - 1) {
                        textAnchor = "end";
                    }
                    return (React.createElement("g", { className: "axis-x-group", key: date.toString() },
                        React.createElement("line", { className: "axis-x-grid", x1: x(date), x2: x(date), y1: "0", y2: areas.h }),
                        React.createElement("text", { className: "axis-x-text", x: x(date), y: areas.h + 16, textAnchor: textAnchor }, date.getFullYear())));
                })),
                React.createElement("g", { className: "axis-y" }, getYTicks(bounds.min, bounds.value).map((tick, idx, arr) => {
                    return (React.createElement("g", { className: "axis-y-group", key: tick },
                        React.createElement("line", { className: "axis-y-grid", y1: y(tick), y2: y(tick), x1: "0", x2: idx === arr.length - 1 ? 0 : width }),
                        React.createElement("text", { className: "axis-y-text", x: 0, y: y(tick) - 5, textAnchor: "start" }, arr.length > 5 && idx % 2 === 1
                            ? ""
                            : formatNumber(tick / bounds.multiple, 1).replace(/(,|\.)0$/, ""))));
                })),
                React.createElement("rect", { width: width, height: areas.h, onMouseOver: eventShowTooltip, onMouseOut: eventHideTooltip, onMouseMove: onMouseMove, onTouchStart: eventShowTooltip, onTouchEnd: eventHideTooltip, onTouchMove: onTouchMove, style: { fill: "none", pointerEvents: "all" } })),
            React.createElement("text", { className: "plot-vertical-entity", x: areas.w - 6, y: 10 },
                "(",
                new Intl.NumberFormat(props.locale, {
                    style: "currency",
                    notation: "compact",
                    currency: props.currency,
                })
                    .format(bounds.value)
                    .replace(/&nbsp;/g, " ")
                    .split("")
                    .filter((char) => !/\d|,|\./.test(char))
                    .join("")
                    .trim(),
                ")"),
            React.createElement("defs", null,
                React.createElement("linearGradient", { id: "gradient", x1: "0%", y1: "-200%", x2: "0%", y2: "100%", spreadMethod: "pad" },
                    React.createElement("stop", { offset: "20%", stopColor: "rgb(14,87,165)", stopOpacity: "0.8" }),
                    React.createElement("stop", { offset: "90%", stopColor: "rgb(6,114,229)", stopOpacity: "0.1" }))))));
}
